import { getLabel } from "utils/dcfExport/details";
import { getProvCode } from "utils/weather";

/** Coerce directory names to a trimmed string. Some records store name as a non-string. */
export const getSortableName = (name) => String(name ?? "").trim();

/** Dashboard Sort Comparators
 * 
 * @param {string} column - The column to sort by
 * @param {string} order - The order to sort by
 * @returns {function} - A function that sorts the data by the given column and order
 */

/** Model Dashboard Sort Comparator */
export const getModelSortComparator = (column, order) => {
    if (column === "name") {
        return ([, a], [, b]) => {
            const nameA = getSortableName(a?.name);
            const nameB = getSortableName(b?.name);

            return order === "asc"
                ? nameA.localeCompare(nameB, undefined, { numeric: true })
                : nameB.localeCompare(nameA, undefined, { numeric: true });
        };
    }
    if (column === "address") {
        return ([, a], [, b]) =>
            order === "asc"
                ? (a.address || "").localeCompare(b.address || "", undefined, { sensitivity: "base" })
                : (b.address || "").localeCompare(a.address || "", undefined, { sensitivity: "base" });
    }
    if (column === "lastEdited") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.lastEdited || 0).getTime();
            const dateB = new Date(b.lastEdited || 0).getTime();

            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    }
};

/** Parametric Analysis Dashboard Sort Comparator */
export const getParametricAnalysisSortComparator = (column, order) => {
    if (column === "name") {
        return ([, a], [, b]) => {
            const nameA = getSortableName(a?.name);
            const nameB = getSortableName(b?.name);

            return order === "asc"
                ? nameA.localeCompare(nameB, undefined, { numeric: true })
                : nameB.localeCompare(nameA, undefined, { numeric: true });
        };
    }
    if (column === "lastEdited") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.lastEdited || 0).getTime();
            const dateB = new Date(b.lastEdited || 0).getTime();

            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    }
};

/** Community Dashboard Sort Comparator */
export const getCommunitySortComparator = (column, order) => {
    if (column === "name") {
        return ([, a], [, b]) => {
            const nameA = getSortableName(a?.name);
            const nameB = getSortableName(b?.name);

            return order === "asc"
                ? nameA.localeCompare(nameB, undefined, { numeric: true })
                : nameB.localeCompare(nameA, undefined, { numeric: true });
        };
    }
    if (column === "size") {
        return ([, a], [, b]) => {
            if (a.type === "community" && b.type === "community") {
                return order === "asc" ? a.totalModels - b.totalModels : b.totalModels - a.totalModels;
            }
            if (a.type === "folder" && b.type === "folder") {
                return order === "asc" ? a.size - b.size : b.size - a.size;
            }
        };
    }
    if (column === "region") {
        return ([, a], [, b]) =>
            order === "asc"
                ? (a.region || "").localeCompare(b.region || "")
                : (b.region || "").localeCompare(a.region || "");
    }
    if (column === "lastEdited") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.lastEdited || 0).getTime();
            const dateB = new Date(b.lastEdited || 0).getTime();
            
            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    };
};

/** Reno Community Model Dashboard Sort Comparator */
export const getRenoCommModelSortComparator = (column, order) => {
    if (column === "name") {
        return ([, a], [, b]) => {
            const nameA = getSortableName(a?.name);
            const nameB = getSortableName(b?.name);
            
            return order === "asc"
                ? nameA.localeCompare(nameB, undefined, { numeric: true })
                : nameB.localeCompare(nameA, undefined, { numeric: true });
        }
    }
    if (column === "userName") {
        return ([, a], [, b]) =>
            order === "asc"
                ? (a.userName || "").localeCompare(b.userName || "")
                : (b.userName || "").localeCompare(a.userName || "");
    }
    if (column === "provTerr") {
        return ([, a], [, b]) => {
            const provCodeA = getProvCode(a.provTerr);
            const provCodeB = getProvCode(b.provTerr);

            return order === "asc"
                ? provCodeA.localeCompare(provCodeB)
                : provCodeB.localeCompare(provCodeA);
        }
    }
    if (column === "currentPhase") {
        return ([, a], [, b]) => order === "asc"
            ? a.currentPhase - b.currentPhase
            : b.currentPhase - a.currentPhase;
    }
    if (column === "lastEdited") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.lastEdited.datetime || 0).getTime();
            const dateB = new Date(b.lastEdited.datetime || 0).getTime();

            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    };
    if (column === "targetCompletionDate") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.targetCompletionDate || 0).getTime();
            const dateB = new Date(b.targetCompletionDate || 0).getTime();

            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    };
};

/** Custom Community Model Dashboard Sort Comparator */
export const getCommModelSortComparator = (column, order) => {
    if (column === "name") {
        return ([, a], [, b]) => {
            const nameA = getSortableName(a?.name);
            const nameB = getSortableName(b?.name);

            return order === "asc"
                ? nameA.localeCompare(nameB, undefined, { numeric: true })
                : nameB.localeCompare(nameA, undefined, { numeric: true });
        };
    }
    if (column === "weatherRegion") {
        return ([, a], [, b]) => {
            const labelA = getLabel("weatherLocation", a.location?.locationId, a.location?.regionId, "v11-12");
            const labelB = getLabel("weatherLocation", b.location?.locationId, b.location?.regionId, "v11-12");

            return order === "asc"
                ? labelA.localeCompare(labelB)
                : labelB.localeCompare(labelA);
        }
    }
    if (column === "countDirection") {
        return ([, a], [, b]) => order === "asc"
            ? a.countDirection.duplicates - b.countDirection.duplicates
            : b.countDirection.duplicates - a.countDirection.duplicates;
    }
    if (column === "status") {
        return ([, a], [, b]) => {
            const statusOrder = ["error", "unknown", "ready"];

            return order === "asc"
                ? statusOrder.indexOf(a.status || "") - statusOrder.indexOf(b.status || "")
                : statusOrder.indexOf(b.status || "") - statusOrder.indexOf(a.status || "");
        }
    }
    if (column === "lastEdited") {
        return ([, a], [, b]) => {
            const dateA = new Date(a.lastEdited.datetime || 0).getTime();
            const dateB = new Date(b.lastEdited.datetime || 0).getTime();

            return order === "asc" ? dateA - dateB : dateB - dateA;
        };
    };
};

/** Helper function to handle sorting in dashboard tables 
 * 
 * @param {string} sortBy - The column to sort by
 * @param {function} setSortBy - A function to set the column to sort by
 * @param {string} sortOrder - The order to sort by
 * @param {function} setSortOrder - A function to set the order to sort by
 * @returns {function} - A function that sorts the data by the given column and order
*/
export const getDashboardSortHandler = (sortBy, setSortBy, sortOrder, setSortOrder) =>
    (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(column);
            setSortOrder(column === "lastEdited" ? "desc" : "asc");
        }
    };